// src/pages/users/ApproverParamsSection.tsx
//
// Sub-seção integrada ao UserFormSidePanelContent (bloco "Requisições")
// para configurar os parâmetros de aprovação de um usuário aprovador:
// precoMaximo, categoriasPermitidas, produtosExcluidos, ativo.
//
// Endpoint do backend (api-blueltSystem):
//   GET /v1/permissoes/{usuarioId}/parametros-aprovacao
//   PUT /v1/permissoes/{usuarioId}/parametros-aprovacao
//
// Endpoints auxiliares de busca:
//   GET /v1/categorias-connection?codigoCategoria=<>&categoria=<>&page=&size=
//   GET /v1/produtos-connection?codigo=<>&nomeProduto=<>&page=&size=
//
// Política de carga: GET dispara no mount apenas se usuarioId estiver
// definido (edição). Para usuários novos (sem id), a seção mostra aviso
// "salve o usuário primeiro" e omite os campos.
import React, { useCallback, useEffect, useState } from 'react';
import { apiGet } from '@/services/api';
import { showAlert } from '@/contexts/DialogContext';
import PaginatedSearchMultiSelect from '@/components/common/PaginatedSearchMultiSelect';
import {
  Categoria,
  Produto,
  UsuarioParametrosAprovacao,
  UsuarioParametrosAprovacaoUpsert,
} from '@/types/aprovacao/Aprovacao';
import { formatDateTime } from '@/utils/formatUtils';
import axios from 'axios';
import './ApproverParamsSection.css';

interface ApproverParamsSectionProps {
  usuarioId: number | null;
  theme: 'dark' | 'light';
  disabled?: boolean;
  // Gate de permissao: quando false, todos os campos viram read-only
  // (disabled + bg vermelho no numerico) e o payload emitido fica null
  // — parent skipa o PUT em /v1/permissoes/{id}/parametros-aprovacao.
  podeEditar: boolean;
  // Emite o payload "lean" pronto para o PUT sempre que o usuário edita.
  // null = ainda não carregado/inicializado OU sem permissao (parent não deve enviar).
  onChange: (params: UsuarioParametrosAprovacaoUpsert | null) => void;
}

const ApproverParamsSection: React.FC<ApproverParamsSectionProps> = ({
  usuarioId,
  theme,
  disabled,
  podeEditar,
  onChange,
}) => {
  const [precoMaximo, setPrecoMaximo] = useState<string>('');
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [ativo, setAtivo] = useState<boolean>(false);
  const [dataAtualizacao, setDataAtualizacao] = useState<string | null>(null);
  const [atualizadoPorNome, setAtualizadoPorNome] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Carga inicial ou em troca de usuarioId
  useEffect(() => {
    if (usuarioId === null || usuarioId === undefined) {
      setInitialized(false);
      onChange(null);
      return;
    }
    let cancelado = false;
    const carregar = async () => {
      setIsLoading(true);
      try {
        const res = await apiGet(`/v1/permissoes/${usuarioId}/parametros-aprovacao`);
        if (cancelado) return;
        const dados: UsuarioParametrosAprovacao = res.data;
        setPrecoMaximo(String(dados.precoMaximo ?? ''));
        // Nomes já vêm resolvidos do GET (api-blueltSystem #55). null (código
        // órfão) coalesce para '' — o render dos chips já cai em '(sem nome)'.
        setCategorias(
          (dados.categoriasPermitidas ?? []).map((c) => ({
            codCategoria: c.codCategoria,
            nomeCategoria: c.nomeCategoria ?? '',
          }))
        );
        setProdutos(
          (dados.produtosExcluidos ?? []).map((pe) => ({
            codProduto: pe.codProduto,
            codCategoria: pe.codCategoria,
            nomeProduto: pe.nomeProduto ?? '',
            nomeCategoria: pe.nomeCategoria ?? '',
          }))
        );
        setAtivo(dados.ativo ?? false);
        setDataAtualizacao(dados.dataAtualizacao);
        setAtualizadoPorNome(dados.atualizadoPorNome ?? null);
        setInitialized(true);
      } catch (err) {
        if (cancelado) return;
        const msgRaw = axios.isAxiosError(err) ? (err.response?.data?.error ?? '') : '';
        const msg = String(msgRaw);
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const isPermissionDenied =
          status === 403 ||
          msg.toLowerCase().includes('nao possui permissao') ||
          msg.toLowerCase().includes('não possui permissão') ||
          msg.toLowerCase().includes('editar_parametros_aprovacao');
        if (isPermissionDenied) {
          setPrecoMaximo('');
          setCategorias([]);
          setProdutos([]);
          setAtivo(false);
          setDataAtualizacao(null);
          setAtualizadoPorNome(null);
          setInitialized(true);
        } else if (axios.isAxiosError(err) && err.response?.status === 400) {
          if (msg.toLowerCase().includes('nao encontrad') || msg.toLowerCase().includes('não encontrad')) {
            setPrecoMaximo('');
            setCategorias([]);
            setProdutos([]);
            setAtivo(false);
            setDataAtualizacao(null);
            setAtualizadoPorNome(null);
            setInitialized(true);
          } else if (msg) {
            await showAlert(msg, { variant: 'error' });
          } else {
            await showAlert('Erro ao carregar parâmetros de aprovação.', { variant: 'error' });
          }
        } else {
          await showAlert('Erro ao carregar parâmetros de aprovação.', { variant: 'error' });
        }
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
    // onChange é estável o suficiente; usuarioId é o gatilho real
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioId]);

  // Emite o payload lean para o parent sempre que o estado interno muda
  useEffect(() => {
    if (!initialized) return;
    if (!podeEditar) {
      onChange(null);
      return;
    }
    const trimmed = precoMaximo.trim();
    let precoMaximoPayload: number | null;
    if (trimmed === '') {
      precoMaximoPayload = null;
    } else {
      const n = Number(trimmed);
      precoMaximoPayload = Number.isFinite(n) ? n : null;
    }
    const payload: UsuarioParametrosAprovacaoUpsert = {
      precoMaximo: precoMaximoPayload,
      categoriasPermitidas: categorias.length === 0 ? null : categorias.map((c) => c.codCategoria),
      produtosExcluidos:
        produtos.length === 0
          ? null
          : produtos.map((p) => ({ codProduto: p.codProduto, codCategoria: p.codCategoria })),
      ativo,
    };
    onChange(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precoMaximo, categorias, produtos, ativo, initialized, podeEditar]);

  const fetchCategorias = useCallback(
    async (page: number, busca: string): Promise<{ items: Categoria[]; totalItems: number }> => {
      const params = new URLSearchParams({ page: page.toString(), size: '20' });
      if (busca.trim() !== '') {
        params.append('codigoCategoria', busca.trim());
        params.append('categoria', busca.trim());
      }
      const res = await apiGet(`/v1/categorias-connection?${params.toString()}`);
      const items: Categoria[] = res.data.content || [];
      return { items, totalItems: res.data.page?.totalElements ?? 0 };
    },
    []
  );

  const fetchProdutos = useCallback(
    async (page: number, busca: string): Promise<{ items: Produto[]; totalItems: number }> => {
      const params = new URLSearchParams({ page: page.toString(), size: '20' });
      if (busca.trim() !== '') {
        params.append('codigo', busca.trim());
        params.append('nomeProduto', busca.trim());
      }
      const res = await apiGet(`/v1/produtos-connection?${params.toString()}`);
      const items: Produto[] = res.data.content || [];
      return { items, totalItems: res.data.page?.totalElements ?? 0 };
    },
    []
  );

  // Empty state: usuario nao salvo ainda
  if (usuarioId === null || usuarioId === undefined) {
    return (
      <div className={`approver-section ${theme}`}>
        <h4 className="approver-section__title">Parâmetros aprovação de compra</h4>
        <p className="approver-section__empty">
          Salve o usuário primeiro para configurar os parâmetros de aprovação.
        </p>
      </div>
    );
  }

  // Loading state: primeira carga
  if (isLoading && !initialized) {
    return (
      <div className={`approver-section ${theme}`}>
        <h4 className="approver-section__title">Parâmetros aprovação de compra</h4>
        <p className="approver-section__loading">Carregando parâmetros...</p>
      </div>
    );
  }

  const gateBlocked = !podeEditar;
  // Cascata: campos so editaveis quando ativo=true E sem gate global.
  const fieldsDisabled = disabled || gateBlocked || !ativo;
  // Toggle "Aprovador ativo" só responde ao gate global / disabled externo,
  // nao a ele mesmo — caso contrario nao seria possivel reativar.
  const toggleDisabled = disabled || gateBlocked;
  const isLoadingRefresh = isLoading && initialized;

  const rootClass = [
    'approver-section',
    theme,
    !ativo ? 'approver-section--inactive' : '',
    isLoadingRefresh ? 'approver-section--loading' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass}>
      <div className="approver-section__header">
        <div className="approver-section__header-col">
          <label className="approver-section__toggle">
            <input
              type="checkbox"
              checked={ativo}
              onChange={(e) => setAtivo(e.target.checked)}
              disabled={toggleDisabled}
            />
            <span className="approver-section__toggle-label">
              Parâmetros aprovação de compra
            </span>
          </label>
          <div className="approver-section__price-row">
            <label className="approver-section__price-label" htmlFor="approver-preco-maximo">
              Preço máximo por requisição:
            </label>
            <div className="approver-section__price-wrapper">
              <span className="approver-section__price-prefix">R$</span>
              <input
                id="approver-preco-maximo"
                className={`approver-section__price-input${gateBlocked ? ' approver-section__price-input--gate-blocked' : ''}`}
                type="number"
                min="0.01"
                step="0.01"
                value={precoMaximo}
                onChange={(e) => setPrecoMaximo(e.target.value)}
                disabled={fieldsDisabled}
                placeholder="0,00"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="approver-section__grid">
        <div className="approver-section__field">
          <PaginatedSearchMultiSelect<Categoria>
            label="Categorias permitidas"
            placeholder="Deixe vazio para permitir todas as categorias."
            fetchFunction={fetchCategorias}
            selectedItems={categorias}
            onSelectionChange={setCategorias}
            getItemKey={(c) => String(c.codCategoria)}
            renderItem={(c) => `${c.codCategoria || '—'} — ${c.nomeCategoria || '(sem nome)'}`}
            renderChip={(c) => `${c.codCategoria || '—'} — ${c.nomeCategoria || '(sem nome)'}`}
            theme={theme}
            disabled={fieldsDisabled}
          />
        </div>

        <div className="approver-section__field">
          <PaginatedSearchMultiSelect<Produto>
            label="Produtos excluídos"
            placeholder="Produtos que este aprovador NÃO poderá aprovar."
            fetchFunction={fetchProdutos}
            selectedItems={produtos}
            onSelectionChange={setProdutos}
            getItemKey={(p) => `${p.codProduto}_${p.codCategoria}`}
            renderItem={(p) => `${p.codProduto || '—'}-${p.codCategoria || '—'} — ${p.nomeProduto || '(sem nome)'}`}
            renderChip={(p) => `${p.codProduto || '—'}-${p.codCategoria || '—'} — ${p.nomeProduto || '(sem nome)'}`}
            theme={theme}
            disabled={fieldsDisabled}
          />
        </div>
      </div>

      {dataAtualizacao && (
        <div className="approver-section__footer">
          <span className="approver-section__footer-icon" aria-hidden="true">ⓘ</span>
          <span>
            Última atualização: {formatDateTime(dataAtualizacao)}
            {atualizadoPorNome ? ` (${atualizadoPorNome})` : ''}
          </span>
        </div>
      )}
    </div>
  );
};

export default ApproverParamsSection;
